import { useState } from 'react'
import { Plus, Trash, Scales } from '@phosphor-icons/react'
import { useI18n } from '@/i18n'
import { newId } from '@/lib/ids'
import { Banner, Button, ChoiceGroup, Field, IntegerInput, PhotoCapture, PickerSheet } from '@/ui'
import { ActionBar } from '../../components/GateScreen'
import { useCodeLabel } from '../../components/labels'
import { counts } from '../../model/delivery'
import { pickMoistureBags } from '../../model/moisture'
import { mintLot } from '../../data/actions'
import { useWizard } from './context'

/** F4.3 — बोरी जाँच · Check bags: count, failed bags (reason + photo), probe draws, moisture bags. */
export function StepCheck() {
  const { t } = useI18n()
  const { d, dispatch, roster, startDispute } = useWizard()
  const code = useCodeLabel()
  const [busy, setBusy] = useState(false)
  const { brought, failed, good } = counts(d)
  const limit = roster.config.bags_confirm_above
  const needsConfirm = brought > limit && !d.bags_brought_confirmed
  const tooFewForTagged = brought < d.bags.length

  const repick = (goodBags: number) =>
    dispatch({ type: 'moisture_bags', nos: goodBags > 0 ? pickMoistureBags(goodBags) : null })

  const ok =
    brought >= 1 &&
    !needsConfirm &&
    !tooFewForTagged &&
    failed <= brought &&
    d.failed_bags.every((f) => f.reason_code && f.photo_media_id) &&
    (good === 0 || d.probe_draws_done)

  const next = async () => {
    setBusy(true)
    try {
      const twoLots = !d.another_field && good > 0 && failed > 0 && !d.conv_lot
      const conv = twoLots ? await mintLot(d.paper?.slip_time ?? Date.now()) : null
      dispatch({
        type: 'check_done',
        moisture_bag_nos: d.moisture_bag_nos ?? pickMoistureBags(good),
        conv_lot: conv,
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Field
        label={t('gate.check.brought')}
        required
        hint={t('gate.check.broughtHint')}
        error={tooFewForTagged ? t('gate.check.belowTagged', { count: d.bags.length }) : undefined}
      >
        <IntegerInput
          size="touch"
          maxDigits={3}
          value={d.bags_brought}
          onChange={(n) => {
            dispatch({ type: 'bags_brought', n })
            repick(Math.max(0, (n ?? 0) - failed))
          }}
        />
      </Field>
      {brought > limit && (
        <ChoiceGroup
          label={t('gate.check.confirmMany', { count: brought })}
          options={[{ value: 'yes', label: t('gate.check.confirmMany', { count: brought }) }]}
          value={d.bags_brought_confirmed ? 'yes' : null}
          onChange={() => dispatch({ type: 'bags_brought', n: brought, confirmed: true })}
        />
      )}

      <div className="flex flex-col gap-3">
        <div className="text-[14px] font-semibold">{t('gate.check.failedBags')}</div>
        {d.failed_bags.map((f, i) => (
          <div
            key={f.id}
            className="flex flex-col gap-2 rounded-card border border-danger/30 bg-danger-wash/40 p-3"
          >
            <div className="flex items-center justify-between">
              <span className="font-semibold text-danger">
                {t('gate.check.failedN', { n: i + 1 })}
              </span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => startDispute({ about: 'GRADE', bag_position: i + 1 })}
                >
                  {t('gate.dispute.button')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={t('gate.check.removeFailed')}
                  onClick={() => {
                    dispatch({ type: 'remove_failed_bag', id: f.id })
                    repick(good + 1)
                  }}
                >
                  <Trash aria-hidden="true" />
                </Button>
              </div>
            </div>
            <Field label={t('gate.check.reason')} required>
              <PickerSheet
                title={t('gate.check.reason')}
                value={f.reason_code}
                onChange={(v) =>
                  dispatch({ type: 'update_failed_bag', id: f.id, patch: { reason_code: v } })
                }
                options={roster.config.red_line_reasons.map((r) => ({
                  value: r.code,
                  label: code(r),
                }))}
              />
            </Field>
            <Field label={t('gate.check.photo')} required hint={t('gate.check.photoHint')}>
              <PhotoCapture
                value={f.photo_media_id}
                onChange={(id) =>
                  dispatch({ type: 'update_failed_bag', id: f.id, patch: { photo_media_id: id } })
                }
              />
            </Field>
          </div>
        ))}
        <Button
          variant="secondary"
          size="touch"
          leadingIcon={<Plus aria-hidden="true" />}
          disabled={failed >= brought}
          onClick={() => {
            dispatch({ type: 'add_failed_bag', id: newId() })
            repick(good - 1)
          }}
        >
          {t('gate.check.addFailed')}
        </Button>
        {failed > 0 && <p className="text-[13px] text-muted">{t('gate.check.redTie')}</p>}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-card border border-rule bg-surface px-3 py-2">
          <div className="text-[12px] text-muted">{t('gate.check.goodBags')}</div>
          <div className="font-mono text-[24px]">{good}</div>
        </div>
        <div className="rounded-card border border-rule bg-surface px-3 py-2">
          <div className="text-[12px] text-muted">{t('gate.check.failedCount')}</div>
          <div className="font-mono text-[24px] text-danger">{failed}</div>
        </div>
      </div>
      {brought > 0 && good === 0 && <Banner tone="info">{t('gate.check.allFailed')}</Banner>}

      {good > 0 && !d.another_field && d.moisture_bag_nos && (
        <Banner tone="info" title={t('gate.check.moistureBags')}>
          <span className="inline-flex items-center gap-1.5">
            <Scales aria-hidden="true" />
            {d.moisture_bag_nos[0] === d.moisture_bag_nos[1]
              ? t('gate.check.bagOne', { a: d.moisture_bag_nos[0]! })
              : t('gate.check.bagPair', { a: d.moisture_bag_nos[0]!, b: d.moisture_bag_nos[1]! })}
          </span>
        </Banner>
      )}

      {good > 0 && (
        <Field label={t('gate.check.probe')} required>
          <ChoiceGroup
            options={[{ value: 'yes', label: t('gate.check.probeDone') }]}
            value={d.probe_draws_done ? 'yes' : null}
            onChange={() => dispatch({ type: 'probe_draws', done: !d.probe_draws_done })}
          />
        </Field>
      )}
      {good > 0 && d.probe_draws_done && (
        <Button
          size="sm"
          variant="ghost"
          className="self-start"
          onClick={() => dispatch({ type: 'probe_draws', done: false })}
        >
          {t('gate.check.probeUndo')}
        </Button>
      )}

      <ActionBar>
        <Button size="xl" fullWidth disabled={!ok} loading={busy} onClick={() => void next()}>
          {t('common.next')}
        </Button>
      </ActionBar>
    </div>
  )
}
